import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    CircularProgress,
    Divider,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
    InputAdornment,
} from '@mui/material'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import DownloadIcon from '@mui/icons-material/Download'
import EventIcon from '@mui/icons-material/Event'
import LocalActivityIcon from '@mui/icons-material/LocalActivity'
import PrintIcon from '@mui/icons-material/Print'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import SearchIcon from '@mui/icons-material/Search'
import { QRCodeCanvas } from 'qrcode.react'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { useWalletSafe } from '../contexts/WalletContext'

const ORANGE = '#FF8A00'
const SURFACE = 'rgba(16,11,38,0.92)'
const BORDER = '1px solid rgba(255,255,255,0.08)'

const LOCAL_COUNTRIES = new Set(['SL', 'SIERRA LEONE', 'LR', 'LIBERIA', 'GN', 'GUINEA', 'GM', 'GAMBIA'])
const LOCAL_CURRENCY_BY_COUNTRY = {
    SL: 'SLE',
    'SIERRA LEONE': 'SLE',
    LR: 'LRD',
    LIBERIA: 'LRD',
    GN: 'GNF',
    GUINEA: 'GNF',
    GM: 'GMD',
    GAMBIA: 'GMD',
}

const normalizeCountry = (value) => String(value || '').trim().toUpperCase()
const money = (amount, currency) => `${currency} ${Number(amount || 0).toFixed(2)}`

const localCurrencyFor = (country, fallback = 'SLE') => (
    LOCAL_CURRENCY_BY_COUNTRY[normalizeCountry(country)] || fallback
)
const isLocalEventCountry = (country) => LOCAL_COUNTRIES.has(normalizeCountry(country))
const eventBannerUrl = (event) => String(event?.event_banner_url || '').trim()
const eventEndTime = (item) => {
    const raw = item?.ends_at || item?.starts_at || item?.date
    if (!raw) return null
    const time = new Date(raw).getTime()
    return Number.isNaN(time) ? null : time
}
const isNotEnded = (item) => {
    const time = eventEndTime(item)
    return time === null || time >= Date.now()
}

const Row = ({ label, value, accent = false }) => (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography sx={{ color: 'rgba(255,255,255,0.66)', fontWeight: 700 }}>{label}</Typography>
        <Typography sx={{ color: accent ? ORANGE : '#fff', fontWeight: accent ? 900 : 800, textAlign: 'right' }}>{value}</Typography>
    </Stack>
)

const ticketQrPayload = (ticket) => ticket.qr_payload || JSON.stringify({
    type: 'mojopayr_event_ticket',
    ticket_no: ticket.ticket_no,
    event_id: ticket.event_id,
    status: ticket.status,
})

export default function Tickets() {
    const location = useLocation()
    const { user } = useAuth()
    const wallet = useWalletSafe()
    const [activeTab, setActiveTab] = useState('events')
    const [events, setEvents] = useState([])
    const [myTickets, setMyTickets] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadingTickets, setLoadingTickets] = useState(true)
    const [error, setError] = useState('')
    const [buyingId, setBuyingId] = useState('')
    const [quantityById, setQuantityById] = useState({})
    const [success, setSuccess] = useState('')
    const [searchQuery, setSearchQuery] = useState('')

    const queryParams = new URLSearchParams(location.search)
    const requestedEventId = queryParams.get('event') || ''
    const requestedCode = queryParams.get('code') || ''

    const userCountry = normalizeCountry(user?.country)
    const isDiaspora = userCountry ? !LOCAL_COUNTRIES.has(userCountry) : false
    const senderCurrency = isDiaspora
        ? String(user?.baseCurrency || 'USD').toUpperCase()
        : localCurrencyFor(userCountry || 'SL')

    const loadEvents = async () => {
        setLoading(true)
        setError('')
        try {
            const response = await api.get('/events', {
                params: isDiaspora ? {} : { country: userCountry || 'SL' },
            })
            const rows = Array.isArray(response.data?.data) ? response.data.data : []
            const cleanedRows = rows
                    .filter(isNotEnded)
                    .sort((a, b) => {
                        if (requestedEventId && String(a.id) === String(requestedEventId)) return -1
                        if (requestedEventId && String(b.id) === String(requestedEventId)) return 1
                        const left = new Date(a.starts_at || a.date || '2999-12-31').getTime()
                        const right = new Date(b.starts_at || b.date || '2999-12-31').getTime()
                        return left - right
                    })
            setEvents(cleanedRows)
            if (requestedEventId && cleanedRows.some((event) => String(event.id) === String(requestedEventId))) {
                setSuccess(`Event code loaded${requestedCode ? `: ${requestedCode}` : ''}.`)
            }
        } catch (e) {
            setError(e?.response?.data?.message || 'Unable to load event tickets.')
        } finally {
            setLoading(false)
        }
    }

    const loadMyTickets = async () => {
        setLoadingTickets(true)
        try {
            const response = await api.get('/tickets')
            const rows = Array.isArray(response.data?.data) ? response.data.data : []
            setMyTickets(rows.filter(isNotEnded))
        } catch (e) {
            setError(e?.response?.data?.message || 'Unable to load your tickets.')
        } finally {
            setLoadingTickets(false)
        }
    }

    useEffect(() => {
        loadEvents()
        loadMyTickets()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCountry, isDiaspora, requestedEventId, requestedCode])

    const balance = wallet?.getBalance?.(senderCurrency) || 0

    const quoteFor = (event) => {
        const quantity = Number(quantityById[event.id] || 1)
        const eventCurrency = String(event.currency || localCurrencyFor(event.country)).toUpperCase()
        const ticketValue = Number(event.price || event.ticket_price || 0) * quantity
        const senderBase = isDiaspora && senderCurrency !== eventCurrency ? ticketValue : ticketValue
        const serviceFee = Number((senderBase * 0.05).toFixed(2))
        const transferFee = isDiaspora && isLocalEventCountry(event.country)
            ? Number((Math.max(senderBase * 0.0115 + 1.2, 1).toFixed(2)))
            : 0
        const fee = serviceFee + transferFee
        return {
            quantity,
            eventCurrency,
            ticketValue,
            senderBase,
            fee,
            serviceFee,
            transferFee,
            total: senderBase + fee,
            exchangeRate: senderBase > 0 ? ticketValue / senderBase : 1,
        }
    }

    const purchase = async (event) => {
        const quote = quoteFor(event)
        if (balance < quote.total) {
            setError(`Insufficient ${senderCurrency} wallet balance for this ticket.`)
            return
        }

        setBuyingId(String(event.id))
        setError('')
        setSuccess('')
        try {
            const response = await api.post(`/events/${event.id}/purchase`, {
                quantity: quote.quantity,
                sender_currency: senderCurrency,
                sender_amount: Number(quote.senderBase.toFixed(2)),
                fee: Number(quote.fee.toFixed(2)),
                total_deducted: Number(quote.total.toFixed(2)),
                exchange_rate: Number(quote.exchangeRate.toFixed(8)),
            })
            setSuccess(`Ticket purchased. Ref ${response.data?.reference || response.data?.order?.order_no || event.title}`)
            setActiveTab('my-tickets')
            await wallet?.refreshWallet?.()
            await loadEvents()
            await loadMyTickets()
        } catch (e) {
            setError(e?.response?.data?.message || 'Ticket purchase failed.')
        } finally {
            setBuyingId('')
        }
    }

    const downloadTicket = (ticket) => {
        const lines = [
            'MojoPayr Event Ticket',
            `Ticket No: ${ticket.ticket_no}`,
            `Event: ${ticket.event_title || 'Event'}`,
            `Section: ${ticket.section_name || 'General'}`,
            `Seat/Label: ${ticket.assignment_label || '-'}`,
            `Status: ${ticket.status}`,
            `Amount: ${money(ticket.price, ticket.currency)}`,
            `Date: ${ticket.starts_at ? new Date(ticket.starts_at).toLocaleString() : '-'}`,
            `Venue: ${ticket.venue_name || ticket.city || ticket.country || '-'}`,
        ]
        const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${ticket.ticket_no || 'mojopayr-ticket'}.txt`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    const activeEvents = useMemo(() => {
        const query = searchQuery.trim().toLowerCase()
        if (!query) return events

        return events.filter((event) => [
            event.title,
            event.description,
            event.venue_name,
            event.venue,
            event.city,
            event.country,
            event.organizer_name,
            event.organizer,
            event.category,
        ].filter(Boolean).join(' ').toLowerCase().includes(query))
    }, [events, searchQuery])

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="lg">
                <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                        <Box>
                            <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: { xs: 34, md: 46 } }}>Tickets</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.72)', mt: 1 }}>
                                Upcoming event tickets with wallet payment, receipt proof, and validation-ready records.
                            </Typography>
                        </Box>
                        <Chip label={`Wallet ${money(balance, senderCurrency)}`} sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, bgcolor: 'rgba(255,138,0,0.14)', color: ORANGE, fontWeight: 900 }} />
                    </Stack>

                    <TextField
                        fullWidth
                        placeholder="Search events"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.58)' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: '#fff',
                                bgcolor: 'rgba(91,46,255,0.16)',
                                borderRadius: 3,
                            },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.08)' },
                        }}
                    />

                    <Tabs
                        value={activeTab}
                        onChange={(_, value) => setActiveTab(value)}
                        sx={{
                            '& .MuiTab-root': { color: 'rgba(255,255,255,0.64)', fontWeight: 900, textTransform: 'none' },
                            '& .Mui-selected': { color: `${ORANGE} !important` },
                            '& .MuiTabs-indicator': { bgcolor: ORANGE },
                            '@media print': { display: 'none' },
                        }}
                    >
                        <Tab value="events" icon={<EventIcon />} iconPosition="start" label="Events" />
                        <Tab value="my-tickets" icon={<LocalActivityIcon />} iconPosition="start" label="My Tickets" />
                    </Tabs>

                    {error ? <Alert severity="error">{error}</Alert> : null}
                    {success ? <Alert severity="success">{success}</Alert> : null}

                    {activeTab === 'events' ? (loading ? (
                        <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress sx={{ color: ORANGE }} /></Box>
                    ) : activeEvents.length === 0 ? (
                        <Card sx={{ bgcolor: SURFACE, border: BORDER, borderRadius: 5 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography sx={{ color: '#fff', fontWeight: 900 }}>
                                    {searchQuery.trim() ? 'No events found' : 'No upcoming tickets available.'}
                                </Typography>
                            </CardContent>
                        </Card>
                    ) : (
                        <Stack spacing={2}>
                            {activeEvents.map((event) => {
                                const quote = quoteFor(event)
                                const startsAt = event.starts_at || event.date
                                return (
                                    <Card key={event.id} sx={{ bgcolor: SURFACE, border: BORDER, borderRadius: 5 }}>
                                        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
                                                <Box sx={{ flex: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: 190,
                                                            borderRadius: 3,
                                                            mb: 2,
                                                            border: '1px solid rgba(255,255,255,0.08)',
                                                            overflow: 'hidden',
                                                            display: 'grid',
                                                            placeItems: 'center',
                                                            position: 'relative',
                                                            bgcolor: 'rgba(91,46,255,0.16)',
                                                        }}
                                                    >
                                                        <EventIcon sx={{ color: ORANGE, fontSize: 58, opacity: 0.82 }} />
                                                        {eventBannerUrl(event) ? (
                                                            <Box
                                                                component="img"
                                                                src={eventBannerUrl(event)}
                                                                alt={event.title || 'Event banner'}
                                                                loading="lazy"
                                                                onError={(e) => {
                                                                    if (process.env.NODE_ENV === 'development') {
                                                                        // eslint-disable-next-line no-console
                                                                        console.warn('Event banner failed to load', eventBannerUrl(event))
                                                                    }
                                                                    e.currentTarget.remove()
                                                                }}
                                                                sx={{
                                                                    position: 'absolute',
                                                                    inset: 0,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        ) : null}
                                                    </Box>
                                                    <Stack direction="row" spacing={1.2} alignItems="center">
                                                        <ConfirmationNumberIcon sx={{ color: ORANGE }} />
                                                        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 22 }}>{event.title}</Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, color: 'rgba(255,255,255,0.66)' }}>
                                                        <EventIcon fontSize="small" />
                                                        <Typography>{startsAt ? new Date(startsAt).toLocaleString() : 'Date to be confirmed'}</Typography>
                                                    </Stack>
                                                    <Typography sx={{ color: 'rgba(255,255,255,0.66)', mt: 1 }}>{event.venue_name || event.venue || event.city || event.country}</Typography>
                                                </Box>

                                                <Stack spacing={1.2} sx={{ minWidth: { md: 320 } }}>
                                                    <TextField
                                                        type="number"
                                                        label="Quantity"
                                                        value={quote.quantity}
                                                        onChange={(e) => setQuantityById((current) => ({ ...current, [event.id]: Math.max(1, Number(e.target.value || 1)) }))}
                                                        inputProps={{ min: 1 }}
                                                        sx={{ '& .MuiInputBase-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.62)' } }}
                                                    />
                                                    <Row label="Ticket value" value={money(quote.ticketValue, quote.eventCurrency)} />
                                                    <Row label="Wallet charge before fee" value={money(quote.senderBase, senderCurrency)} />
                                                    <Row label="Service fee" value={money(quote.serviceFee, senderCurrency)} />
                                                    {quote.transferFee > 0 ? <Row label="Transfer fee" value={money(quote.transferFee, senderCurrency)} /> : null}
                                                    <Row label="Total payable" value={money(quote.total, senderCurrency)} accent />
                                                    <Button
                                                        variant="contained"
                                                        disabled={buyingId === String(event.id) || balance < quote.total}
                                                        onClick={() => purchase(event)}
                                                        sx={{ bgcolor: ORANGE, color: '#080C14', fontWeight: 900, '&:hover': { bgcolor: '#ff9f2e' } }}
                                                    >
                                                        {buyingId === String(event.id) ? 'Processing...' : 'Confirm Payment'}
                                                    </Button>
                                                </Stack>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                )
                            })}
                        </Stack>
                    )) : (
                        <MyTicketsPanel
                            tickets={myTickets}
                            loading={loadingTickets}
                            onDownload={downloadTicket}
                        />
                    )}
                </Stack>
            </Container>
        </Box>
    )
}

function MyTicketsPanel({ tickets, loading, onDownload }) {
    const [selectedId, setSelectedId] = useState('')
    const selectedTicket = useMemo(
        () => tickets.find((ticket) => String(ticket.id) === String(selectedId)) || tickets[0] || null,
        [tickets, selectedId]
    )

    useEffect(() => {
        if (!selectedId && tickets[0]) {
            setSelectedId(String(tickets[0].id))
        }
    }, [selectedId, tickets])

    if (loading) {
        return <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress sx={{ color: ORANGE }} /></Box>
    }

    if (tickets.length === 0) {
        return (
            <Card sx={{ bgcolor: SURFACE, border: BORDER, borderRadius: 5 }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 900 }}>No purchased tickets yet.</Typography>
                </CardContent>
            </Card>
        )
    }

    return (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
            <Card sx={{ bgcolor: SURFACE, border: BORDER, borderRadius: 5, flex: 1, '@media print': { display: 'none' } }}>
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 22, mb: 2 }}>Purchased Tickets</Typography>
                    <Stack spacing={1.4}>
                        {tickets.map((ticket) => (
                            <Card
                                key={ticket.id}
                                onClick={() => setSelectedId(String(ticket.id))}
                                sx={{
                                    cursor: 'pointer',
                                    bgcolor: String(selectedTicket?.id) === String(ticket.id) ? 'rgba(255,138,0,0.13)' : 'rgba(255,255,255,0.045)',
                                    border: String(selectedTicket?.id) === String(ticket.id) ? '1px solid rgba(255,138,0,0.5)' : BORDER,
                                    borderRadius: 3,
                                }}
                            >
                                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 900 }}>{ticket.event_title || 'Event Ticket'}</Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.64)', fontSize: 14 }}>{ticket.ticket_no}</Typography>
                                    <Typography sx={{ color: ORANGE, fontWeight: 900, mt: 0.7 }}>{money(ticket.price, ticket.currency)}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </CardContent>
            </Card>

            {selectedTicket ? (
                <TicketDetails
                    ticket={selectedTicket}
                    onDownload={() => onDownload(selectedTicket)}
                    onPrint={() => window.print()}
                />
            ) : null}
        </Stack>
    )
}

function TicketDetails({ ticket, onDownload, onPrint }) {
    return (
        <Card sx={{ bgcolor: SURFACE, border: BORDER, borderRadius: 5, flex: 1.2, '@media print': { bgcolor: '#fff', color: '#111', boxShadow: 'none', border: 'none' } }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={2.2}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                        <QrCode2Icon sx={{ color: ORANGE, '@media print': { color: '#111' } }} />
                        <Typography sx={{ color: '#fff', fontWeight: 950, fontSize: 26, '@media print': { color: '#111' } }}>QR Ticket</Typography>
                    </Stack>
                    <Box sx={{ bgcolor: '#fff', borderRadius: 4, p: 2, alignSelf: 'flex-start' }}>
                        <QRCodeCanvas value={ticketQrPayload(ticket)} size={220} includeMargin />
                    </Box>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.045)', border: BORDER, borderRadius: 4, '@media print': { bgcolor: '#fff', border: '1px solid #ddd' } }}>
                        <CardContent>
                            <Stack spacing={1.2}>
                                <Row label="Ticket No." value={ticket.ticket_no} />
                                <Row label="Event" value={ticket.event_title || 'Event Ticket'} />
                                <Row label="Section" value={ticket.section_name || 'General'} />
                                <Row label="Seat / Label" value={ticket.assignment_label || '-'} />
                                <Row label="Status" value={ticket.status || '-'} />
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.13)' }} />
                                <Row label="Amount" value={money(ticket.price, ticket.currency)} accent />
                                <Row label="Date" value={ticket.starts_at ? new Date(ticket.starts_at).toLocaleString() : '-'} />
                                <Row label="Venue" value={ticket.venue_name || ticket.city || ticket.country || '-'} />
                            </Stack>
                        </CardContent>
                    </Card>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ '@media print': { display: 'none' } }}>
                        <Button startIcon={<DownloadIcon />} variant="outlined" onClick={onDownload} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.24)', fontWeight: 900 }}>
                            Download
                        </Button>
                        <Button startIcon={<PrintIcon />} variant="contained" onClick={onPrint} sx={{ bgcolor: ORANGE, color: '#080C14', fontWeight: 900 }}>
                            Print
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    )
}
